import type { LockerversePublicEvent } from "@lockerverse/sdk/public-events";
import {
  CalendarDays,
  CalendarX2,
  Check,
  Copy,
  Facebook,
  Forward,
  Mail,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { getAccentContrast } from "./accent-contrast.ts";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";
import type { UseLockerversePublicEventsResult } from "./use-lockerverse-public-events.ts";

export type LockerversePublicEventsProps = {
  events: UseLockerversePublicEventsResult;
  /** The visible heading. Defaults to Events. */
  title?: string;
  showHeading?: boolean;
  emptyMessage?: string;
  /** Used to show dates and times. Defaults to America/New_York. */
  timeZone?: string;
  theme?: LockerverseTheme;
  style?: LockerverseStyle;
  className?: string;
};

function formatDate(value: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone,
    year: "numeric",
  }).format(new Date(value));
}

function formatTime(value: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  }).format(new Date(value));
}

function getLink(value: string | null) {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function getShareUrl(item: LockerversePublicEvent, past: boolean) {
  const link = getLink(item.link);
  if (link) {
    return link;
  }
  const url = new URL(window.location.href);
  if (past) {
    url.searchParams.set("lockerverseEvents", "past");
  } else {
    url.searchParams.delete("lockerverseEvents");
  }
  url.searchParams.delete("lockerverseEventOffset");
  url.hash = `event-${item.id}`;
  return url.toString();
}

function withAccentContrast(style?: LockerverseStyle) {
  const accent = style?.["--lockerverse-accent"];
  if (!accent || style?.["--lockerverse-accent-contrast"] !== undefined) {
    return style;
  }
  const contrast = getAccentContrast(accent);
  return contrast
    ? { ...style, "--lockerverse-accent-contrast": contrast }
    : style;
}

function copyWithExecCommand(value: string) {
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  try {
    input.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    input.remove();
  }
}

function EventActions({
  item,
  past,
}: {
  item: LockerversePublicEvent;
  past: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [manualShareUrl, setManualShareUrl] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareMenuAbove, setShareMenuAbove] = useState(false);
  const shareActionsRef = useRef<HTMLDivElement>(null);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);
  const link = getLink(item.link);
  const isWithinShareActions = (target: EventTarget | null) =>
    target instanceof Node && shareActionsRef.current?.contains(target);

  useEffect(() => {
    if (!shareUrl) {
      return;
    }
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!isWithinShareActions(event.target)) {
        setShareUrl(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShareUrl(null);
        if (shareActionsRef.current?.contains(document.activeElement)) {
          shareTriggerRef.current?.focus();
        }
      }
    };
    const closeOnFocusOutside = (event: FocusEvent) => {
      if (!isWithinShareActions(event.target)) {
        setShareUrl(null);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("focusin", closeOnFocusOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("focusin", closeOnFocusOutside);
    };
  }, [shareUrl]);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  async function copyEventLink() {
    if (!shareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setShareUrl(null);
      shareTriggerRef.current?.focus();
    } catch {
      if (copyWithExecCommand(shareUrl)) {
        setCopied(true);
        setShareUrl(null);
        shareTriggerRef.current?.focus();
      } else {
        setManualShareUrl(shareUrl);
      }
    }
  }

  return (
    <div className="lockerverse-public-events__actions" ref={shareActionsRef}>
      {link ? (
        <a
          className="lockerverse-public-events__link"
          href={link}
          rel="noopener noreferrer"
          target="_blank"
        >
          View event <span aria-hidden="true">↗</span>
        </a>
      ) : null}
      <button
        aria-expanded={shareUrl !== null}
        aria-label={`Share ${item.name}`}
        className="lockerverse-public-events__share"
        data-copied={copied}
        onClick={() => {
          if (!shareUrl && shareTriggerRef.current) {
            setShareMenuAbove(
              window.innerHeight -
                shareTriggerRef.current.getBoundingClientRect().bottom <
                72
            );
          }
          setShareUrl(shareUrl ? null : getShareUrl(item, past));
          setCopied(false);
          setManualShareUrl(null);
        }}
        ref={shareTriggerRef}
        title={`Share ${item.name}`}
        type="button"
      >
        {copied ? (
          <Check aria-hidden="true" size={22} strokeWidth={2} />
        ) : (
          <Forward aria-hidden="true" size={22} strokeWidth={1.9} />
        )}
      </button>
      {copied ? (
        <span
          className="lockerverse-public-events__visually-hidden"
          role="status"
        >
          Link copied
        </span>
      ) : null}
      {shareUrl ? (
        <fieldset
          className="lockerverse-public-events__share-menu"
          data-above={shareMenuAbove}
        >
          <legend className="lockerverse-public-events__visually-hidden">
            Share {item.name}
          </legend>
          <button
            aria-label="Copy event link"
            onClick={copyEventLink}
            title="Copy link"
            type="button"
          >
            <Copy aria-hidden="true" size={18} />
          </button>
          <a
            aria-label="Share by email"
            href={`mailto:?subject=${encodeURIComponent(item.name)}&body=${encodeURIComponent(shareUrl)}`}
            title="Email"
          >
            <Mail aria-hidden="true" size={18} />
          </a>
          <a
            aria-label="Share on Facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            rel="noopener noreferrer"
            target="_blank"
            title="Facebook"
          >
            <Facebook aria-hidden="true" size={18} />
          </a>
          <a
            aria-label="Share on X"
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(item.name)}`}
            rel="noopener noreferrer"
            target="_blank"
            title="X"
          >
            <span>𝕏</span>
          </a>
          {manualShareUrl ? (
            <label className="lockerverse-public-events__copy-link">
              Select and copy this event link
              <input
                autoFocus
                onFocus={(event) => event.currentTarget.select()}
                readOnly
                value={manualShareUrl}
              />
            </label>
          ) : null}
        </fieldset>
      ) : null}
    </div>
  );
}

function PublicEventRow({
  item,
  timeZone,
  active,
  past = false,
}: {
  item: LockerversePublicEvent;
  timeZone: string;
  active: boolean;
  past?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const start = new Date(item.startAt);
  const month = new Intl.DateTimeFormat("en-US", {
    month: "short",
    timeZone,
  }).format(start);
  const day = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    timeZone,
  }).format(start);
  const year = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
  }).format(start);
  const startDateLabel = formatDate(item.startAt, timeZone);
  const sameDay = startDateLabel === formatDate(item.endAt, timeZone);
  const endLabel = sameDay
    ? formatTime(item.endAt, timeZone)
    : `${formatDate(item.endAt, timeZone)} at ${formatTime(item.endAt, timeZone)}`;
  const imageUrl = getLink(item.imageUrl);
  const canExpand = (item.description?.length ?? 0) > 130;

  return (
    <li className="lockerverse-public-events__item" id={`event-${item.id}`}>
      <article className="lockerverse-public-events__row">
        {imageUrl ? (
          <img
            alt=""
            className="lockerverse-public-events__image"
            height={124}
            loading="lazy"
            src={imageUrl}
            width={154}
          />
        ) : (
          <time
            className="lockerverse-public-events__date"
            dateTime={item.startAt}
          >
            <span className="lockerverse-public-events__month">{month}</span>
            <span className="lockerverse-public-events__day">{day}</span>
            <span className="lockerverse-public-events__year">{year}</span>
          </time>
        )}
        <div className="lockerverse-public-events__content">
          <div className="lockerverse-public-events__heading">
            <h3>{item.name}</h3>
            {active ? (
              <span className="lockerverse-public-events__live">Live now</span>
            ) : null}
          </div>
          <p className="lockerverse-public-events__schedule">
            {imageUrl ? (
              <>
                <time dateTime={item.startAt}>{startDateLabel}</time>
                <span aria-hidden="true"> · </span>
              </>
            ) : null}
            <time dateTime={item.startAt}>
              {formatTime(item.startAt, timeZone)}
            </time>
            <span aria-hidden="true"> — </span>
            <time dateTime={item.endAt}>{endLabel}</time>
          </p>
          {item.location ? (
            <p className="lockerverse-public-events__place">{item.location}</p>
          ) : null}
          {item.description ? (
            <>
              <p
                className={[
                  "lockerverse-public-events__description",
                  canExpand && !expanded
                    ? "lockerverse-public-events__description--clamped"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {item.description}
              </p>
              {canExpand ? (
                <button
                  aria-expanded={expanded}
                  className="lockerverse-public-events__more"
                  onClick={() => setExpanded(!expanded)}
                  type="button"
                >
                  {expanded ? "Show less" : "Read more"}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
        <EventActions item={item} past={past} />
      </article>
    </li>
  );
}

function EventRows({
  items,
  now,
  timeZone,
  past = false,
}: {
  items: LockerversePublicEvent[];
  now: number;
  timeZone: string;
  past?: boolean;
}) {
  return (
    <ol className="lockerverse-public-events__list">
      {items.map((item) => (
        <PublicEventRow
          active={
            !past &&
            new Date(item.startAt).getTime() <= now &&
            new Date(item.endAt).getTime() > now
          }
          item={item}
          key={item.id}
          past={past}
          timeZone={timeZone}
        />
      ))}
    </ol>
  );
}

function LoadMoreButton({
  disabled,
  label,
  onClick,
}: {
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="lockerverse-public-events__load-more"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {disabled ? "Loading…" : label}
    </button>
  );
}

function CurrentEventsPanel({
  events,
  view,
  tabId,
  sort,
  now,
  emptyMessage,
  timeZone,
}: {
  events: UseLockerversePublicEventsResult;
  view: "current" | "past";
  tabId: string;
  sort: string | undefined;
  now: number;
  emptyMessage: string;
  timeZone: string;
}) {
  const availableEvents = events.events ?? [];
  const activeEvents = availableEvents.filter(
    (item) =>
      new Date(item.startAt).getTime() <= now &&
      new Date(item.endAt).getTime() > now
  );
  const upcomingEvents = availableEvents.filter(
    (item) => new Date(item.startAt).getTime() > now
  );
  const currentEvents = availableEvents.filter(
    (item) => new Date(item.endAt).getTime() > now
  );
  const empty = sort
    ? currentEvents.length === 0
    : activeEvents.length === 0 && upcomingEvents.length === 0;

  return (
    <section
      aria-labelledby={`${tabId}-current-tab`}
      hidden={view !== "current"}
      id={`${tabId}-current-panel`}
    >
      {events.loading && !events.events ? (
        <p role="status">Loading events…</p>
      ) : null}
      {events.error ? (
        <div role="alert">
          <p>{events.error.message}</p>
          <button onClick={events.refresh} type="button">
            Try again
          </button>
        </div>
      ) : null}
      {events.events && empty ? (
        <p className="lockerverse-public-events__empty">{emptyMessage}</p>
      ) : null}
      {sort && currentEvents.length > 0 ? (
        <EventRows items={currentEvents} now={now} timeZone={timeZone} />
      ) : null}
      {!sort && activeEvents.length > 0 ? (
        <div className="lockerverse-public-events__group">
          <EventRows items={activeEvents} now={now} timeZone={timeZone} />
        </div>
      ) : null}
      {!sort && upcomingEvents.length > 0 ? (
        <div className="lockerverse-public-events__group">
          <EventRows items={upcomingEvents} now={now} timeZone={timeZone} />
        </div>
      ) : null}
      {events.hasMore ? (
        <LoadMoreButton
          disabled={events.loadingMore}
          label="Load more events"
          onClick={events.loadMore}
        />
      ) : null}
    </section>
  );
}

function CombinedEventsPanel({
  events,
  title,
  now,
  emptyMessage,
  timeZone,
}: {
  events: UseLockerversePublicEventsResult;
  title: string;
  now: number;
  emptyMessage: string;
  timeZone: string;
}) {
  return (
    <section aria-label={title}>
      {events.loading && !events.events ? (
        <p role="status">Loading events…</p>
      ) : null}
      {events.error ? (
        <div role="alert">
          <p>{events.error.message}</p>
          <button onClick={events.refresh} type="button">
            Try again
          </button>
        </div>
      ) : null}
      {events.events && events.events.length === 0 ? (
        <p className="lockerverse-public-events__empty">{emptyMessage}</p>
      ) : null}
      {events.events && events.events.length > 0 ? (
        <EventRows items={events.events} now={now} timeZone={timeZone} />
      ) : null}
      {events.hasMore ? (
        <LoadMoreButton
          disabled={events.loadingMore}
          label="Load more events"
          onClick={events.loadMore}
        />
      ) : null}
    </section>
  );
}

function PastEventsPanel({
  tabId,
  view,
  pastEvents,
  pastLoaded,
  pastLoading,
  pastError,
  pastHasMore,
  pastOffset,
  loadPast,
  now,
  timeZone,
}: {
  tabId: string;
  view: "current" | "past";
  pastEvents: LockerversePublicEvent[];
  pastLoaded: boolean;
  pastLoading: boolean;
  pastError: string | null;
  pastHasMore: boolean;
  pastOffset: number;
  loadPast: (offset: number, reset?: boolean) => Promise<void>;
  now: number;
  timeZone: string;
}) {
  return (
    <section
      aria-labelledby={`${tabId}-past-tab`}
      hidden={view !== "past"}
      id={`${tabId}-past-panel`}
    >
      {pastError ? (
        <div role="alert">
          <p>{pastError}</p>
          <button
            onClick={() => loadPast(pastLoaded ? pastOffset : 0, !pastLoaded)}
            type="button"
          >
            Try again
          </button>
        </div>
      ) : null}
      {pastLoading && !pastLoaded ? (
        <p role="status">Loading past events…</p>
      ) : null}
      {pastLoaded && pastEvents.length === 0 ? (
        <p className="lockerverse-public-events__empty">No past events yet.</p>
      ) : null}
      {pastEvents.length > 0 ? (
        <EventRows items={pastEvents} now={now} past timeZone={timeZone} />
      ) : null}
      {pastHasMore ? (
        <LoadMoreButton
          disabled={pastLoading}
          label="Load more past events"
          onClick={() => loadPast(pastOffset)}
        />
      ) : null}
    </section>
  );
}

/** Optional public list that can be embedded in any React site. */
export function LockerversePublicEvents({
  events,
  title = "Events",
  showHeading = true,
  emptyMessage = "No events yet.",
  timeZone = "America/New_York",
  theme = "dark",
  style,
  className,
}: LockerversePublicEventsProps) {
  const [now, setNow] = useState(() => Date.now());
  const [view, setView] = useState<"current" | "past">("current");
  const [pastEvents, setPastEvents] = useState<LockerversePublicEvent[]>([]);
  const [pastOffset, setPastOffset] = useState(0);
  const [pastHasMore, setPastHasMore] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastTarget, setPastTarget] = useState<string | null>(null);
  const tabId = useId();
  const { showTabs, includePast, from, to, sort } = events.config;
  const configuredPast = Boolean(from || to || sort);
  const pastQueryKey = JSON.stringify([from, to, sort, showTabs, includePast]);
  const previousPastQueryKey = useRef(pastQueryKey);
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);
  const pastRequestId = useRef(0);
  const [hashRevision, setHashRevision] = useState(0);
  const autoLookup = useRef({ key: "", pages: 0 });

  const loadPast = useCallback(
    async (offset: number, reset = false) => {
      pastRequestId.current += 1;
      const requestId = pastRequestId.current;
      setPastLoading(true);
      setPastError(null);
      try {
        const page = configuredPast
          ? await events.client.search({
              from,
              limit: 20,
              offset,
              sort: sort ?? "endAtDesc",
              timeScope: "past",
              to,
            })
          : await events.client.listPast({ offset });
        if (requestId !== pastRequestId.current) {
          return;
        }
        setPastEvents((current) =>
          reset ? page.events : [...current, ...page.events]
        );
        setPastOffset(offset + page.events.length);
        setPastHasMore(page.hasMore);
        setPastLoaded(true);
      } catch (error) {
        if (requestId !== pastRequestId.current) {
          return;
        }
        setPastError(
          error instanceof Error ? error.message : "Unable to load past events."
        );
      } finally {
        if (requestId === pastRequestId.current) {
          setPastLoading(false);
        }
      }
    },
    [events.client, configuredPast, from, to, sort]
  );

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (initializedFromUrl) {
      return;
    }
    setInitializedFromUrl(true);
    if (!(showTabs && includePast)) {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get("lockerverseEvents") !== "past") {
      return;
    }
    setView("past");
    setPastTarget(url.hash.slice(1));
    loadPast(0, true);
  }, [initializedFromUrl, loadPast, showTabs, includePast]);

  useEffect(() => {
    if (previousPastQueryKey.current === pastQueryKey) {
      return;
    }
    previousPastQueryKey.current = pastQueryKey;
    pastRequestId.current += 1;
    setPastEvents([]);
    setPastOffset(0);
    setPastHasMore(false);
    setPastLoaded(false);
    if (!(showTabs && includePast)) {
      setView("current");
    } else if (view === "past") {
      loadPast(0, true);
    }
  }, [pastQueryKey, showTabs, includePast, view, loadPast]);

  useEffect(() => {
    const onHashChange = () => setHashRevision((current) => current + 1);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (showTabs) {
      return;
    }
    const hash = window.location.hash.slice(1);
    const lookupKey = `${pastQueryKey}:${hash}`;
    if (autoLookup.current.key !== lookupKey) {
      autoLookup.current = { key: lookupKey, pages: 0 };
    }
    if (!hash.startsWith("event-")) {
      return;
    }
    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ block: "center" });
    } else if (
      !(events.loading || events.loadingMore || events.error) &&
      events.hasMore &&
      autoLookup.current.pages < 5
    ) {
      autoLookup.current.pages += 1;
      events.loadMore();
    }
  }, [
    events.events,
    events.error,
    events.hasMore,
    events.loading,
    events.loadingMore,
    events.loadMore,
    hashRevision,
    pastQueryKey,
    showTabs,
  ]);

  useEffect(() => {
    if (!showTabs || view !== "current") {
      return;
    }
    const hash = window.location.hash.slice(1);
    if (!hash.startsWith("event-")) {
      return;
    }
    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ block: "center" });
    } else if (
      !(events.loading || events.loadingMore || events.error) &&
      events.hasMore
    ) {
      events.loadMore();
    }
  }, [
    events.events,
    events.error,
    events.hasMore,
    events.loading,
    events.loadingMore,
    events.loadMore,
    showTabs,
    view,
  ]);

  useEffect(() => {
    if (
      !showTabs ||
      view !== "past" ||
      !pastTarget ||
      pastLoading ||
      pastError ||
      !pastLoaded
    ) {
      return;
    }
    const target = document.getElementById(pastTarget);
    if (target) {
      target.scrollIntoView({ block: "center" });
      setPastTarget(null);
    } else if (pastHasMore) {
      loadPast(pastOffset);
    } else {
      setPastTarget(null);
    }
  }, [
    loadPast,
    pastError,
    pastHasMore,
    pastLoaded,
    pastLoading,
    pastOffset,
    pastTarget,
    showTabs,
    view,
  ]);

  function openPast() {
    setView("past");
    if (pastLoaded || pastLoading) {
      return;
    }
    loadPast(0, true);
  }

  return (
    <section
      aria-busy={showTabs && view === "past" ? pastLoading : events.loading}
      aria-label={title}
      className={["lockerverse-public-events", className]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-tabs={showTabs}
      data-lockerverse-theme={theme}
      style={withAccentContrast(style)}
    >
      <div className="lockerverse-public-events__layout">
        {showHeading ? <h2>{title}</h2> : null}
        {showTabs ? (
          <nav
            aria-label="Event views"
            className="lockerverse-public-events__tabs"
          >
            <button
              aria-controls={`${tabId}-current-panel`}
              aria-pressed={view === "current"}
              className="lockerverse-public-events__tab"
              id={`${tabId}-current-tab`}
              onClick={() => setView("current")}
              type="button"
            >
              <CalendarDays aria-hidden="true" size={18} />
              Upcoming Events
            </button>
            {includePast ? (
              <button
                aria-controls={`${tabId}-past-panel`}
                aria-pressed={view === "past"}
                className="lockerverse-public-events__tab"
                id={`${tabId}-past-tab`}
                onClick={openPast}
                type="button"
              >
                <CalendarX2 aria-hidden="true" size={18} />
                Past Events
              </button>
            ) : null}
          </nav>
        ) : null}
        {showTabs ? (
          <>
            <CurrentEventsPanel
              emptyMessage={emptyMessage}
              events={events}
              now={now}
              sort={sort}
              tabId={tabId}
              timeZone={timeZone}
              view={view}
            />
            {includePast ? (
              <PastEventsPanel
                loadPast={loadPast}
                now={now}
                pastError={pastError}
                pastEvents={pastEvents}
                pastHasMore={pastHasMore}
                pastLoaded={pastLoaded}
                pastLoading={pastLoading}
                pastOffset={pastOffset}
                tabId={tabId}
                timeZone={timeZone}
                view={view}
              />
            ) : null}
          </>
        ) : (
          <CombinedEventsPanel
            emptyMessage={emptyMessage}
            events={events}
            now={now}
            timeZone={timeZone}
            title={title}
          />
        )}
      </div>
    </section>
  );
}
